import type { DashboardWidgetSpan } from "@ibada/dashboard-engine"

/** Stable identifiers for the real Finance Overview widgets (registry keys + DnD ids). */
export enum FinanceWidgetId {
  /** Per-branch income/expense/net — GLOBAL scope only. */
  BranchPerformance = "branch-performance",
  /** Each branch's SHARE of church-wide income — GLOBAL scope only. */
  BranchContribution = "branch-contribution",
  /** Branches whose expenses exceeded their income — GLOBAL scope only. */
  BranchDeficit = "branch-deficit",
  /** Branches that recorded no money at all this period — GLOBAL scope only. */
  SilentBranches = "silent-branches",
  /** Each branch's SHARE of church-wide spending — GLOBAL scope only. */
  BranchSpendShare = "branch-spend-share",
  /** Postings made and activities running, church-wide — GLOBAL scope only. */
  ChurchActivityVolume = "church-activity-volume",
  /** Net per activity across every branch — GLOBAL scope only. */
  ChurchActivityFinance = "church-activity-finance",
  TotalCash = "total-cash",
  MoneyInOut = "money-in-out",
  AccountWatchlist = "account-watchlist",
  FundWatchlist = "fund-watchlist",
  BudgetHealth = "budget-health",
  PledgeSummary = "pledge-summary",
  NeedsAttention = "needs-attention",
  ReconciliationStatus = "reconciliation-status",
  AuditAssurance = "audit-assurance",
  QuickActions = "quick-actions",
  // Current / Quick / Debt ratio widgets and the Cash Balance Trend stub were removed: the first three depend
  // on liability data the system does not track, and Cash Trend had no backend. Operating Margin (ledger-sourced)
  // stays.
  OperatingMargin = "operating-margin",
  IncomeByAccount = "income-by-category",
  ExpensesByAccount = "expenses-by-category",
  RecentTransactions = "recent-transactions",
  // IBA-157 budget performance. All five read ONE endpoint and render server-computed figures.
  BudgetVsActual = "budget-vs-actual",
  IncomeBudgetVsActual = "income-budget-vs-actual",
  FundUtilization = "fund-utilization",
  BudgetLinePerformance = "budget-line-performance",
  BudgetOverspend = "budget-overspend",
  // The F3 church-wide tiles. Self-drawn (D4 style) and GLOBAL only; own ids, never the older widgets' ids above.
  /** Church cash today, split between the branches and the parish, with its monthly trend. */
  ChurchCash = "church-cash",
  /** Per-branch income and expense bars with the net at the right. */
  ChurchBranches = "church-branches",
  /** Each branch's share of church income, as one segmented bar. Percentages only. */
  ChurchShare = "church-share",
  /** Branches spending more than they earn; the worst one's last three months. */
  ChurchStruggling = "church-struggling",
  /** Branches with no money recorded this period, and when they last did. */
  ChurchSilent = "church-silent",
  /** Net per activity across every branch, most costly first. */
  ChurchActivities = "church-activities",
  /** What each branch remitted to the parish, against the previous period. */
  ChurchRemittances = "church-remittances",
  // The F1 branch tiles. Self-drawn (D4 style) and BRANCH only; own ids, never the older widgets' ids above.
  /** Cash today, each account with its reconciliation state, nine weeks of money in and out. */
  BranchTreasury = "branch-treasury",
  /** What blocks a clean close: missing documents, accounts to reconcile, transfers to accept, the ledger. */
  BranchAttention = "branch-attention",
  /** Income by account, and the top source's share against the period before. */
  BranchIncome = "branch-income",
  /** Expenses by line, and the line that moved the most. */
  BranchExpenses = "branch-expenses",
  /** Of every 100 received, what was spent and kept; six months of the kept share. */
  BranchMargin = "branch-margin",
  /** The budget widgets merged: planned against realised per line with the pace marker. */
  BranchBudget = "branch-budget",
  /** What each fund may still commit. */
  BranchFunds = "branch-funds",
  /** Collected against promised, late pledges, the two largest. */
  BranchPledges = "branch-pledges",
  /** The last few operations of the period. */
  BranchRecent = "branch-recent",
  /** Per account: last reconciled, and whether that is current, late or never. */
  BranchReconciliation = "branch-reconciliation",
  /** Ready to close? A verdict from the checks the system can prove. */
  BranchControl = "branch-control",
}

/**
 * Default flow order (the single grid places these row-major, 1–3 columns per breakpoint, each widget
 * spanning its configured width). Only registered widgets render; the list grows as widgets are added.
 */
export const FINANCE_WIDGET_SEEDS: readonly FinanceWidgetId[] = [
  FinanceWidgetId.TotalCash,
  FinanceWidgetId.MoneyInOut,
  FinanceWidgetId.NeedsAttention,
  FinanceWidgetId.OperatingMargin,
  FinanceWidgetId.IncomeByAccount,
  FinanceWidgetId.ExpensesByAccount,
  FinanceWidgetId.AccountWatchlist,
  FinanceWidgetId.BudgetHealth,
  FinanceWidgetId.RecentTransactions,
  FinanceWidgetId.FundWatchlist,
  FinanceWidgetId.PledgeSummary,
  FinanceWidgetId.QuickActions,
  FinanceWidgetId.ReconciliationStatus,
  FinanceWidgetId.AuditAssurance,
  FinanceWidgetId.BudgetVsActual,
  FinanceWidgetId.IncomeBudgetVsActual,
  FinanceWidgetId.FundUtilization,
  FinanceWidgetId.BudgetLinePerformance,
  FinanceWidgetId.BudgetOverspend,
]

/**
 * Curated, most-important subset shown when inspecting another branch's finance read-only (Branch Details) —
 * the parish admin needs the headline health signals, not the full customizable board.
 */
export const FINANCE_INSPECTION_WIDGET_SEEDS: readonly FinanceWidgetId[] = [
  FinanceWidgetId.TotalCash,
  FinanceWidgetId.MoneyInOut,
  FinanceWidgetId.IncomeByAccount,
  FinanceWidgetId.ExpensesByAccount,
]

/**
 * The consolidated board's DEFAULT layout: the approved F3 design, three rows on the engine's three tracks.
 *
 * Row 1: church cash (1 track) beside the branches (2). Row 2: who carries the church, who is struggling, who is
 * silent (1 each). Row 3, shorter: activities (1) beside remittances (2); F3 draws these two as halves of a
 * 12-column grid, which three tracks cannot split evenly, so remittances, the wider table, takes two.
 *
 * The older consolidated widgets ({@link FINANCE_GLOBAL_WIDGET_SEEDS}) stay registered and HIDDEN, so every one
 * of them is still offered under "Add widgets".
 */
export const FINANCE_CHURCH_F3_WIDGET_SEEDS: readonly FinanceWidgetId[] = [
  FinanceWidgetId.ChurchCash,
  FinanceWidgetId.ChurchBranches,
  FinanceWidgetId.ChurchShare,
  FinanceWidgetId.ChurchStruggling,
  FinanceWidgetId.ChurchSilent,
  FinanceWidgetId.ChurchActivities,
  FinanceWidgetId.ChurchRemittances,
]

/** Default column spans of the F3 arrangement; a tile not listed takes one track. */
export const FINANCE_CHURCH_F3_SPANS: Readonly<Partial<Record<FinanceWidgetId, DashboardWidgetSpan>>> = {
  [FinanceWidgetId.ChurchBranches]: 2,
  [FinanceWidgetId.ChurchRemittances]: 2,
}

/**
 * The F3 default when branch autonomy withholds the absolutes (ADR-0024): what F3 can say without an amount
 * (each branch's share of income, who has gone silent), plus the postings count the ratio-only board already
 * showed. The money tiles are not in the registry at all for this reader.
 */
export const FINANCE_CHURCH_F3_RATIO_ONLY_SEEDS: readonly FinanceWidgetId[] = [
  FinanceWidgetId.ChurchShare,
  FinanceWidgetId.ChurchSilent,
  FinanceWidgetId.ChurchActivityVolume,
]

/**
 * The consolidated board's saved-layout key, VERSIONED for F3.
 *
 * Layouts saved before F3 live under `finance-overview:global` and name only the older widgets; reconciling one
 * would keep those visible and slot the F3 tiles in around an unrelated arrangement. A fresh key gives every
 * existing user the F3 default; the older widgets stay one click away in "Add widgets", and the old key is left
 * untouched.
 */
export const FINANCE_GLOBAL_LAYOUT_KEY = "finance-overview:global:f3-v1"

/**
 * The older consolidated (Global Finance) widgets, now offered under "Add widgets" beside F3.
 *
 * Deliberately short. These four are the widgets whose backing endpoint aggregates across a branch SET; a
 * five-widget board of true numbers is worth more than a full one where half the cards quietly describe a
 * single branch as though it were the whole church.
 */
export const FINANCE_GLOBAL_WIDGET_SEEDS: readonly FinanceWidgetId[] = [
  FinanceWidgetId.TotalCash,
  FinanceWidgetId.MoneyInOut,
  FinanceWidgetId.IncomeByAccount,
  FinanceWidgetId.ExpensesByAccount,
  FinanceWidgetId.BranchPerformance,
  // The consolidated-only questions: where the money comes from, who is not covering their costs, and who has
  // gone quiet. A workspace total hides all three — the strong branches net out the weak, and a branch that
  // stopped posting looks the same as one with nothing to report.
  FinanceWidgetId.BranchContribution,
  FinanceWidgetId.BranchSpendShare,
  FinanceWidgetId.BranchDeficit,
  FinanceWidgetId.SilentBranches,
  FinanceWidgetId.ChurchActivityFinance,
  FinanceWidgetId.ChurchActivityVolume,
]

/**
 * The board a reader gets when branch autonomy withholds the absolutes (ADR-0024).
 *
 * Not the money board with its cards emptied — that is what produced `$0.00` for a branch holding $1,200,
 * which is not a smaller truth but a different and false one. Autonomy's promise is that the parish still
 * sees HEALTH, so this is what health is made of: the operating margin ratio, who has gone quiet, what needs
 * attention, how much is being recorded, and the audit verdict. Every one of these is scale-free or a count,
 * and none of them can be inverted back into a balance.
 */
export const FINANCE_RATIO_ONLY_WIDGET_SEEDS: readonly FinanceWidgetId[] = [
  FinanceWidgetId.OperatingMargin,
  FinanceWidgetId.NeedsAttention,
  FinanceWidgetId.AuditAssurance,
  FinanceWidgetId.SilentBranches,
  FinanceWidgetId.ChurchActivityVolume,
]

/**
 * The BRANCH board's DEFAULT layout: the approved F1 design, four rows on the engine's three tracks.
 *
 * Row 1: treasury (2 tracks) beside what blocks the close (1). Row 2: where the money comes from, where it goes,
 * the margin. Row 3: budget, funds, pledges (F1 draws 6 + 3 + 3 of twelve columns; three tracks give one each).
 * Row 4: latest operations, reconciliation, control.
 *
 * Every older branch widget ({@link FINANCE_WIDGET_SEEDS}) stays registered and HIDDEN, so it is still offered under
 * "Add widgets".
 */
export const FINANCE_BRANCH_F1_WIDGET_SEEDS: readonly FinanceWidgetId[] = [
  FinanceWidgetId.BranchTreasury,
  FinanceWidgetId.BranchAttention,
  FinanceWidgetId.BranchIncome,
  FinanceWidgetId.BranchExpenses,
  FinanceWidgetId.BranchMargin,
  FinanceWidgetId.BranchBudget,
  FinanceWidgetId.BranchFunds,
  FinanceWidgetId.BranchPledges,
  FinanceWidgetId.BranchRecent,
  FinanceWidgetId.BranchReconciliation,
  FinanceWidgetId.BranchControl,
]

/** Default column spans of the F1 arrangement; a tile not listed takes one track. */
export const FINANCE_BRANCH_F1_SPANS: Readonly<Partial<Record<FinanceWidgetId, DashboardWidgetSpan>>> = {
  [FinanceWidgetId.BranchTreasury]: 2,
}

/**
 * The F1 default when branch autonomy withholds the absolutes (ADR-0024): exactly the ratio-only board this reader
 * had before F1 (the operating margin, the attention counts, quick actions, the audit verdict). Every F1 tile states
 * amounts, so none is in the registry for this reader.
 */
export const FINANCE_BRANCH_F1_RATIO_ONLY_SEEDS: readonly FinanceWidgetId[] = [
  FinanceWidgetId.NeedsAttention,
  FinanceWidgetId.OperatingMargin,
  FinanceWidgetId.QuickActions,
  FinanceWidgetId.AuditAssurance,
]

/**
 * The branch board's saved-layout key, VERSIONED for F1.
 *
 * Layouts saved before F1 live under `finance-overview:branch` and name only the older widgets; reconciling one
 * would keep those visible and slot the F1 tiles in around an unrelated arrangement. A fresh key gives every
 * existing user the F1 default; the older widgets stay one click away in "Add widgets", and the old key is left
 * untouched.
 */
export const FINANCE_BRANCH_LAYOUT_KEY = "finance-overview:branch:f1-v1"

/**
 * Branch Details (a parish reader inspecting one branch, read-only): a curated set of the F1 tiles. No to-do list,
 * no operations list, no pledges: nothing that invites an action on another branch's books.
 */
export const FINANCE_INSPECTION_F1_WIDGET_SEEDS: readonly FinanceWidgetId[] = [
  FinanceWidgetId.BranchTreasury,
  FinanceWidgetId.BranchIncome,
  FinanceWidgetId.BranchExpenses,
  FinanceWidgetId.BranchMargin,
  FinanceWidgetId.BranchBudget,
  FinanceWidgetId.BranchReconciliation,
  FinanceWidgetId.BranchControl,
]

/** Branch Details spans: the treasury takes two tracks, as on the branch's own board. */
export const FINANCE_INSPECTION_F1_SPANS: Readonly<Partial<Record<FinanceWidgetId, DashboardWidgetSpan>>> = FINANCE_BRANCH_F1_SPANS

/** Branch Details when the inspected branch's absolutes are withheld (ADR-0024): its operating margin, a ratio. */
export const FINANCE_INSPECTION_F1_RATIO_ONLY_SEEDS: readonly FinanceWidgetId[] = [FinanceWidgetId.OperatingMargin]

/** Branch Details' saved-layout key, versioned for F1 like the board's (`finance-overview-inspect:branch` before). */
export const FINANCE_INSPECTION_LAYOUT_KEY = "finance-overview-inspect:branch:f1-v1"

/**
 * Widgets Branch Details never registers, so they are not even under "Add widgets": the operational ledger and
 * to-do lists, and the create shortcuts. Inspection is read-only.
 */
export const FINANCE_INSPECTION_EXCLUDED_WIDGETS: readonly FinanceWidgetId[] = [
  FinanceWidgetId.RecentTransactions,
  FinanceWidgetId.NeedsAttention,
  FinanceWidgetId.QuickActions,
  FinanceWidgetId.BranchAttention,
  FinanceWidgetId.BranchRecent,
  FinanceWidgetId.BranchPledges,
]
